import { useState, useEffect, useCallback } from 'react';

export interface RecentCaregiver {
  userId: string;
  name: string;
  avatarUrl: string | null;
  hourlyRate: number;
  experienceYears: number;
  ratingAvg: number;
  regions: string[];
  viewedAt: number;
}

const STORAGE_KEY = 'kc_recent_caregivers';
const MAX_RECENT = 10;

function loadRecent(): RecentCaregiver[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentCaregiver[];
  } catch {
    return [];
  }
}

function saveRecent(list: RecentCaregiver[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // storage full - silently ignore
  }
}

export function useRecentlyViewed() {
  const [recent, setRecent] = useState<RecentCaregiver[]>([]);

  useEffect(() => {
    setRecent(loadRecent());
  }, []);

  const addRecent = useCallback((cg: Omit<RecentCaregiver, 'viewedAt'>) => {
    setRecent((prev) => {
      const filtered = prev.filter((r) => r.userId !== cg.userId);
      const entry: RecentCaregiver = { ...cg, viewedAt: Date.now() };
      const updated = [entry, ...filtered].slice(0, MAX_RECENT);
      saveRecent(updated);
      return updated;
    });
  }, []);

  const clearRecent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setRecent([]);
  }, []);

  return { recent, addRecent, clearRecent };
}